import {inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, debounceTime, Observable, tap} from 'rxjs';
import {ErrorMessage, VpbRekenService} from './rekenclient.api';
import {
  AlgemeneVragen,
  BalansActiva,
  BalansEnResultatenrekening,
  BalansGebouwen,
  BalansMVA,
  BalansOmzetbelasting,
  BalansPassiva,
  BalansVoorziening,
  CorporateTaxEntity,
  Periode,
  ResultatenRekening,
  Vermogensvergelijking,
  VpbAangifte,
  VpbBerekening
} from './api.bokkz.services';
import {MenuInterface} from './opbouw';
import {FormBuilder, FormGroup} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class BokkzVpbDataService implements OnDestroy {

  private apiService = inject(VpbRekenService);

  public corporateTaxEntity = new BehaviorSubject<CorporateTaxEntity>(
    this.getFromSessionStorage<CorporateTaxEntity>('corporateTaxEntity') ||
    ({} as CorporateTaxEntity)
  );

  public corporateTaxEntityVergelijking =
    new BehaviorSubject<CorporateTaxEntity>(
      this.getFromSessionStorage<CorporateTaxEntity>(
        'corporateTaxEntityVergelijking'
      ) || ({} as CorporateTaxEntity)
    );

  public vpbAangifteVergelijking = new BehaviorSubject<VpbAangifte>(
    this.getFromSessionStorage<VpbAangifte>('vpbAangifteVergelijking') ||
    ({} as VpbAangifte)
  );

  public vpbBerekening = new BehaviorSubject<VpbBerekening>(
    this.getFromSessionStorage<VpbBerekening>('vpbBerekening') ||
    ({} as VpbBerekening)
  );

  public vpbBerekeningVergelijking = new BehaviorSubject<VpbBerekening>(
    this.getFromSessionStorage<VpbBerekening>('vpbBerekeningVergelijking') ||
    ({} as VpbBerekening)
  );

    public isFiscaleEenheid = new BehaviorSubject<boolean>(false);
    public hasAgrarisch = new BehaviorSubject<boolean>(false);
    public hasInnovatiebox = new BehaviorSubject<boolean>(false);
    public hasDividends = new BehaviorSubject<boolean>(false);
    public hasWillekeurigAfschrijven = new BehaviorSubject<boolean>(false);
    public hasFunctioneleValuta = new BehaviorSubject<boolean>(false);
    public hasDeelnemingen = new BehaviorSubject<boolean>(false);
    public hasHir = new BehaviorSubject<boolean>(false);
    public hasVoeging = new BehaviorSubject<boolean>(false);
    public hasOntvoeging = new BehaviorSubject<boolean>(false);
    public hasOntbinding = new BehaviorSubject<boolean>(false);
    public hasFeVerliezen = new BehaviorSubject<boolean>(false);
    public hasBronheffing = new BehaviorSubject<boolean>(false);
    public menuInterface = new BehaviorSubject<MenuInterface[]>([]);
    public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
    public aantalValidaties = new BehaviorSubject<number>(0);
    public isDefAangifte = new BehaviorSubject<boolean>(false);
    public balans = {} as BalansEnResultatenrekening;
    public isVerzoek = new BehaviorSubject<boolean>(false);

  public corporateTaxEntity$: Observable<CorporateTaxEntity> = this.corporateTaxEntity.asObservable();
  public vpbBerekening$: Observable<VpbBerekening> = this.vpbBerekening.asObservable();
  public aantalValidaties$: Observable<number> = this.aantalValidaties.asObservable();
  public errorResponse$: Observable<ErrorMessage[]> = this.errorResponse.asObservable();

  public patchCorporateTaxEntity(updater: (entity: CorporateTaxEntity) => CorporateTaxEntity): void {
    const current = this.corporateTaxEntity.value;
    this.corporateTaxEntity.next(updater(current));
  }

  calculateLatestCorporateTaxReturn(): Observable<VpbBerekening> {
    const aangifte = this.corporateTaxEntity.value.aangifte;

    if (aangifte) {
      return this.apiService.getCorporateTaxCalculation(aangifte).pipe(
        tap(result => {
          this.vpbBerekening.next(result);
        })
      );
    } else return new Observable();
  }

  constructor(private fb: FormBuilder) {
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
    const debounceDelay = 500; // Timeout voor toetsenaanslagen
    this.corporateTaxEntity.pipe(
      debounceTime(500),
      tap(value => this.saveToSessionStorage('corporateTaxEntity', value)),
      takeUntilDestroyed()
    ).subscribe();
    this.vpbBerekening = this.vpbBerekening.pipe(
      debounceTime(debounceDelay),
      tap((value) => this.saveToSessionStorage('vpbBerekening', value)),
    ) as BehaviorSubject<VpbBerekening>;
    this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
    this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
  }

    //Voor het ophalen uit de sessionstorage.
    private getFromSessionStorage<T>(key: string): T | null {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    }

    //Voor het opslaan in sessionstorage
    private saveToSessionStorage(key: string, value: any) {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

  ngOnDestroy(): void {
    this.corporateTaxEntity.complete();
    if (this.hasDividends) {
      this.hasDividends?.unsubscribe();
    }
    if (this.hasWillekeurigAfschrijven) {
      this.hasWillekeurigAfschrijven?.unsubscribe();
    }
    if (this.hasFunctioneleValuta) {
      this.hasFunctioneleValuta?.unsubscribe();
    }
    if (this.hasDeelnemingen) {
      this.hasDeelnemingen?.unsubscribe();
    }
    if (this.hasHir) {
      this.hasHir?.unsubscribe();
    }
    if (this.hasVoeging) {
      this.hasVoeging?.unsubscribe();
    }
    if (this.hasOntvoeging) {
      this.hasOntvoeging?.unsubscribe();
    }
    if (this.hasOntbinding) {
      this.hasOntbinding?.unsubscribe();
    }
    if (this.hasFeVerliezen) {
      this.hasFeVerliezen?.unsubscribe();
    }
    if (this.hasBronheffing) {
      this.hasBronheffing?.unsubscribe();
    }
    if (this.menuInterface) {
      this.menuInterface?.unsubscribe();
    }
    if (this.errorResponse) {
      this.errorResponse?.unsubscribe();
    }
    if (this.aantalValidaties) {
      this.aantalValidaties?.unsubscribe();
    }
    if (this.corporateTaxEntity) {
      this.corporateTaxEntity.unsubscribe();
    }
    if (this.hasInnovatiebox) {
      this.hasInnovatiebox?.unsubscribe();
    }
    if (this.hasAgrarisch) {
      this.hasAgrarisch?.unsubscribe();
    }
    if (this.isFiscaleEenheid) {
      this.isFiscaleEenheid?.unsubscribe();
    }
    if (this.vpbBerekeningVergelijking) {
      this.vpbBerekeningVergelijking?.unsubscribe();
    }
    if (this.vpbBerekening) {
      this.vpbBerekening?.unsubscribe();
    }
    if (this.vpbAangifteVergelijking) {
      this.vpbAangifteVergelijking?.unsubscribe();
    }
    if (this.corporateTaxEntityVergelijking) {
      this.corporateTaxEntityVergelijking.unsubscribe();
    }
  }

  updateAangifte(vpbAangifte: VpbAangifte): VpbAangifte {
    const vpbBerekening = this.getFromSessionStorage('vpbBerekening') as VpbBerekening;
    if (vpbAangifte.algemeneVragen == undefined) {
      vpbAangifte.algemeneVragen = {} as AlgemeneVragen;
      vpbAangifte.algemeneVragen.verzoekExplicietStandpunt = false;
      vpbAangifte.algemeneVragen.regelingFunctioneleValuta = false;
      vpbAangifte.algemeneVragen.gebruikInnovatiebox = false;
      vpbAangifte.algemeneVragen.internationaleHoustervennootschap = false;
      vpbAangifte.algemeneVragen.toepassingBesluitBeleggingsinstellingen = false;
      vpbAangifte.algemeneVragen.toepassingBesluitWinstbepalingVerzekeraars = false;
      vpbAangifte.algemeneVragen.activiteitenOndernemingGewijzigd = false;
      vpbAangifte.algemeneVragen.wijzigingWaarderingsstelsel = false;
      vpbAangifte.algemeneVragen.willekeurigAfschijven = false;
      vpbAangifte.algemeneVragen.nieuweOfGewijzigdePensioenregeling = false;
      vpbAangifte.algemeneVragen.pensioenaanspraakVerminderd = false;
      vpbAangifte.algemeneVragen.vervreemdingAfgewaardeerdeVordering = false;
      vpbAangifte.algemeneVragen.aftrekValutaverliesOpDeelnemingen = false;
      vpbAangifte.algemeneVragen.verdelingAandelenkapitaalGewijzigd = false;
      vpbAangifte.algemeneVragen.beperkingVerliesverrekeningInOverdrachtsjaar = false;
      vpbAangifte.algemeneVragen.earnOutRegeling = false;
      vpbAangifte.algemeneVragen.toepassingTegenbewijsRenteaftrek = false;
      vpbAangifte.algemeneVragen.schulden10aBuitenFiscaleEenheid = false;
      vpbAangifte.algemeneVragen.compenserendeHeffing10aSchulden = false;
      vpbAangifte.algemeneVragen.kennisCompenserende10aHeffingOntvanger = false;
      vpbAangifte.algemeneVragen.saldoVorderingenEnSchuldenVerbondenLichamen = 0;
      vpbAangifte.algemeneVragen.kwalificatieBelastbareWinst = false;
      vpbAangifte.algemeneVragen.betaaldBedragVoorActiva = 0;
      vpbAangifte.algemeneVragen.rechtshandelingenMetBuitenlandseAandeelhouders = false;
      vpbAangifte.algemeneVragen.lokaalDossierBedrijfsreorganisatieOfAfdrachten = false;
      vpbAangifte.algemeneVragen.belangInFiscaalTransparanteEntiteit = false;
      vpbAangifte.algemeneVragen.vermogenAfgezonderdInApv = false;
    }

        if (!vpbAangifte.vermogensvergelijking) {
            vpbAangifte.vermogensvergelijking = {} as Vermogensvergelijking;
        }

        if (!vpbAangifte.balansEnResultatenrekening) {
            vpbAangifte.balansEnResultatenrekening = {} as BalansEnResultatenrekening;
        }

        if (vpbAangifte.identificatie !== undefined) {
            if (vpbAangifte.identificatie.aangifteFiscaleEenheid) {
                this.isFiscaleEenheid.next(true);
            }
        }

        this.hasInnovatiebox.next(false);
        if (vpbAangifte.algemeneVragen !== undefined) {
            if (vpbAangifte.algemeneVragen.gebruikInnovatiebox) {
                this.hasInnovatiebox.next(true);
            }
        }

        if (!this.hasInnovatiebox.value) {
            vpbAangifte.specificatieInnovatiebox = undefined;
        }

        this.hasWillekeurigAfschrijven.next(false);
        if (vpbAangifte.algemeneVragen !== undefined) {
            if (vpbAangifte.algemeneVragen.willekeurigAfschijven) {
                this.hasWillekeurigAfschrijven.next(true);
            }
        }
        if (!this.hasWillekeurigAfschrijven.value) {
            vpbAangifte.specificatieWillekeurigeAfschrijving = undefined;
        }

        this.hasFunctioneleValuta.next(false);
        if (vpbAangifte.algemeneVragen !== undefined) {
            if (vpbAangifte.algemeneVragen.regelingFunctioneleValuta) {
                this.hasFunctioneleValuta.next(true);
            }
        }
        if (!this.hasFunctioneleValuta.value) {
            if (vpbAangifte.belastingBedrag?.functioneleValuta) {
                vpbAangifte.belastingBedrag.functioneleValuta = undefined;
            }
        }

        this.hasAgrarisch.next(false);
        if (vpbAangifte.balansEnResultatenrekening !== undefined) {
            if (vpbAangifte.balansEnResultatenrekening.agrarischeActiviteiten) {
                this.hasAgrarisch.next(true);
            }
        }
        if (!this.hasAgrarisch.value) {
            vpbAangifte.specificatieAgrarischeActiviteiten = undefined;
        }

        this.hasDividends.next(false);
        if (vpbAangifte.vermogensvergelijking !== null) {
            if (
                vpbAangifte.vermogensvergelijking.onttrekkingen !== null &&
                vpbAangifte.vermogensvergelijking.onttrekkingen?.at(0)?.bedrag !== undefined &&
                vpbAangifte.vermogensvergelijking.onttrekkingen?.at(0)?.bedrag !== null
            ) {
                this.hasDividends.next(true);
            }
        }

        this.hasDeelnemingen.next(false);
        if (vpbAangifte.balansEnResultatenrekening !== undefined) {
            if (
                vpbAangifte.balansEnResultatenrekening.deelnemingen !== undefined &&
                vpbAangifte.balansEnResultatenrekening.deelnemingen?.at(0)?.slotbalans !== undefined &&
                vpbAangifte.balansEnResultatenrekening.deelnemingen.at(0)?.slotbalans !== null
            ) {
                this.hasDeelnemingen.next(true);
            }
        }

        if (!this.hasDeelnemingen.value) {
            vpbAangifte.specificatieDeelnemingen = undefined;
        }

        this.hasHir.next(false);
        if (vpbAangifte.balansEnResultatenrekening !== undefined) {
            if (
                vpbAangifte.balansEnResultatenrekening.herinvesteringsReserve !== undefined &&
                vpbAangifte.balansEnResultatenrekening.herinvesteringsReserve?.at(0)?.slotbalans !== undefined &&
                vpbAangifte.balansEnResultatenrekening.herinvesteringsReserve.at(0)?.slotbalans !== null
            ) {
                this.hasHir.next(true);
            }
        }

        if (!this.hasHir.value) {
            vpbAangifte.balansEnResultatenrekening.specificatieHerinvesteringsreserve = undefined;
            vpbAangifte.balansEnResultatenrekening.herinvesteringsReserve = undefined;
        }

        if (
            vpbAangifte.balansEnResultatenrekening.kostenEgalisatieReserve !== undefined &&
            vpbAangifte.balansEnResultatenrekening.kostenEgalisatieReserve[0].openingsbalans === undefined &&
            vpbAangifte.balansEnResultatenrekening.kostenEgalisatieReserve[0].slotbalans === undefined
        ) {
            vpbAangifte.balansEnResultatenrekening.kostenEgalisatieReserve = undefined;
        }

        if (
            vpbAangifte.balansEnResultatenrekening.onderhandenWerk !== undefined &&
            vpbAangifte.balansEnResultatenrekening.onderhandenWerk[0].openingsbalans === undefined &&
            vpbAangifte.balansEnResultatenrekening.onderhandenWerk[0].slotbalans === undefined
        ) {
            vpbAangifte.balansEnResultatenrekening.onderhandenWerk = undefined;
        }

        this.hasBronheffing.next(false);
        if (vpbAangifte.belastingBedrag !== undefined) {
            if (vpbAangifte.belastingBedrag.aftrekEldersBelast !== undefined && vpbAangifte.belastingBedrag.aftrekEldersBelast !== null) {
                this.hasBronheffing.next(true);
            }
        }

        this.hasVoeging.next(false);
        if (vpbAangifte.vragenFiscaleEenheid !== undefined) {
            if (vpbAangifte.vragenFiscaleEenheid.voegingMetBestaandeDochtermaatschappijen) {
                this.hasVoeging.next(true);
            }
        }

        if (!this.hasVoeging.value) {
            if (vpbAangifte.specificatieFiscaleEenheid?.specificatieVoeging) {
                vpbAangifte.specificatieFiscaleEenheid.specificatieVoeging = undefined;
            }
        }

        this.hasOntvoeging.next(false);
        if (vpbAangifte.vragenFiscaleEenheid !== undefined) {
            if (vpbAangifte.vragenFiscaleEenheid.ontvoegingVanDochtermaatschappijen) {
                this.hasOntvoeging.next(true);
            }
        }

        if (!this.hasOntvoeging.value) {
            if (vpbAangifte.specificatieFiscaleEenheid?.specificatieOntvoeging) {
                vpbAangifte.specificatieFiscaleEenheid.specificatieOntvoeging = undefined;
            }
        }

        this.hasOntbinding.next(false);
        if (vpbAangifte.vragenFiscaleEenheid !== undefined) {
            if (vpbAangifte.vragenFiscaleEenheid.beeindigingDoorliquidatieDochter) {
                this.hasOntbinding.next(true);
            }
        }

        if (!this.hasOntbinding.value) {
            if (vpbAangifte.specificatieFiscaleEenheid?.specificatieOntbinding) {
                vpbAangifte.specificatieFiscaleEenheid.specificatieOntbinding = undefined;
            }
        }

        this.hasFeVerliezen.next(false);
        if (this.isFiscaleEenheid && vpbAangifte.verliescompensatie !== undefined) {
            if (
                vpbAangifte.verliescompensatie.teVerrekenenGewoneVerliezen !== undefined &&
                vpbAangifte.verliescompensatie.teVerrekenenGewoneVerliezen[0] !== undefined &&
                vpbAangifte.verliescompensatie.teVerrekenenGewoneVerliezen[0].bedrag !== undefined &&
                vpbAangifte.verliescompensatie.teVerrekenenGewoneVerliezen[0].bedrag !== null
            ) {
                this.hasFeVerliezen.next(true);
            }
        }

        if (
            vpbBerekening &&
            vpbBerekening.belastbaarBedrag &&
            vpbBerekening.belastbaarBedrag.belastbaarBedrag != null &&
            vpbBerekening.belastbaarBedrag.belastbaarBedrag > 0
        ) {
            if (vpbAangifte.verliescompensatie && vpbAangifte.verliescompensatie.verrekeningVerliesDitBoekjaar != null) {
                vpbAangifte.verliescompensatie.verrekeningVerliesDitBoekjaar = undefined;
            }
        }

        if (!this.isFiscaleEenheid.value) {
            vpbAangifte.enkelvoudigeResultatenFiscaleEenheid = undefined;
        }

        if (vpbAangifte.balansEnResultatenrekening === undefined) {
            vpbAangifte.balansEnResultatenrekening = {} as BalansEnResultatenrekening;
        }
        if (vpbAangifte.balansEnResultatenrekening.omschrijvingActiviteiten === undefined) {
            vpbAangifte.balansEnResultatenrekening.omschrijvingActiviteiten = 'Activiteiten';
        }
        if (vpbAangifte.vermogensvergelijking === undefined) {
            vpbAangifte.vermogensvergelijking = {} as Vermogensvergelijking;
        }

        if (vpbAangifte.verliescompensatie && vpbAangifte.verliescompensatie.verrekeningVerliesDitBoekjaar) {
            if (vpbAangifte.verliescompensatie.verrekeningVerliesDitBoekjaar === 0) {
                vpbAangifte.verliescompensatie.verrekeningVerliesDitBoekjaar = undefined;
            }
        }

        if (
            vpbAangifte.specificatieInvesteringen &&
            vpbAangifte.specificatieInvesteringen.investeringslijst &&
            vpbAangifte.specificatieInvesteringen.investeringslijst.length === 0
        ) {
            vpbAangifte.specificatieInvesteringen.investeringslijst = undefined;
        }

        if (vpbAangifte.specificatieInvesteringen) {
            const inv = vpbAangifte.specificatieInvesteringen;
            const isEmpty =
                !inv.investeringslijst?.length &&
                inv.kiaCaribischDeelVanNederland == null &&
                inv.investeringsaftrekUitVoorgaandeJaren == null &&
                inv.rdaAftrek == null &&
                inv.desinvesteringsbijtelling == null &&
                inv.investeringsaftrekBijOvernameUitNalatenschap == null;

            if (isEmpty) {
                vpbAangifte.specificatieInvesteringen = undefined;
            }
        }

        this.updateMenuInterface(vpbAangifte);
        return vpbAangifte;
        // this.vpbAangifte.next(vpbAangfite);
    }

    updateVpbAangifteVergelijking(aangifteVorigJaar: VpbAangifte) {
        this.vpbAangifteVergelijking.next(aangifteVorigJaar);
    }

    updateCorporateTaxEntity(corporateTax: CorporateTaxEntity): void {
        if (corporateTax.aangifte) {
            const isVerzoek = corporateTax.taxType === 'VA';

            this.isDefAangifte.next(!isVerzoek);
            this.isVerzoek.next(isVerzoek);

            corporateTax.aangifte = this.updateAangifte(corporateTax.aangifte);
        }

        this.corporateTaxEntity.next(corporateTax);
    }

    getIsVerzoek(corporateTax: CorporateTaxEntity): boolean {
        return corporateTax.taxType === 'VA';
    }

    removeBalansActivaPassiva(corporateTax: CorporateTaxEntity): void {
        if (!corporateTax?.aangifte?.balansEnResultatenrekening) {
            return;
        }

        corporateTax.aangifte.balansEnResultatenrekening.goodwill = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.overigeImmaterieleActiva = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.gebouwenEnTerreinen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.machinesEnInstallaties = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.andereVasteActiva = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.deelnemingen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingenLangGroepsmaatschappijen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingenLangParticipanten = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.overigeFinancieelVasteActiva = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.voorraden = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.onderhandenWerk = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingDebiteuren = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingenOmzetbelasting = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingenKortGroepsmaatschappijen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingenKortParticipanten = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.vorderingenOverig = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.effecten = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.liquideMiddelen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.gestortEnOpgevraagdKapitaal = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.informeelKapitaal = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.agioreserve = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.winstreserves = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.kostenEgalisatieReserve = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.herinvesteringsReserve = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.specificatieHerinvesteringsreserve = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.compartimenteringsreserve = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.overigeReserves = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.garantieVoorzieningen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.pensioenVoorzieningen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.oudedagsVoorzieningen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.overigeVoorzieningen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLanglopendConvertibles = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLanglopendObligaties = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLanglopendGroepsmaatschappijen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLanglopendParticipanten = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLanglopendKredietinstellingen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLanglopendOverig = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.crediteuren = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenOmzetbelasting = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenLoonheffingen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenKortlopendGroepsmaatschappijen = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenKortlopendParticipanten = undefined;
        corporateTax.aangifte.balansEnResultatenrekening.schuldenKortlopendOverig = undefined;

        this.updateCorporateTaxEntity(corporateTax);
    }

    updateCorporateTaxEntityVergelijking(corporateTaxEntityVergelijking: CorporateTaxEntity) {
        this.corporateTaxEntityVergelijking.next(corporateTaxEntityVergelijking);
    }

    updateVpbBerekening(vpbBerekening: VpbBerekening) {
        this.vpbBerekening.next(vpbBerekening);
    }

    updateVpbBerekeningVergelijking(berekeningVorigJaar: VpbBerekening) {
        this.vpbBerekeningVergelijking.next(berekeningVorigJaar);
    }

    updateErrorResponse(errorResponse: ErrorMessage[]) {
        this.errorResponse.next(errorResponse);
    }

    updateAantalValidaties(aantalValidaties: number) {
        this.aantalValidaties.next(aantalValidaties);
    }

  updateMenuInterface(vpbAangfite: VpbAangifte) {
    const def = this.isDefAangifte.value;
    const fe = this.isFiscaleEenheid.value;
    const feBijz = vpbAangfite.vragenFiscaleEenheid?.voegingMetBestaandeDochtermaatschappijen ||
      vpbAangfite.vragenFiscaleEenheid?.ontvoegingVanDochtermaatschappijen ||
      vpbAangfite.vragenFiscaleEenheid?.beeindigingDoorliquidatieDochter ||
      vpbAangfite.vragenFiscaleEenheid?.verzoekMeegevenVerliezenOntvoegdeDochter ||
      vpbAangfite.vragenFiscaleEenheid?.verzoekMeegevenVerliezenvanVerdwijnendeDochter ||
      vpbAangfite.vragenFiscaleEenheid?.verzoekMeegevenVerliezenVerkrijger;
    const menuInterface: MenuInterface[] = [
        {
            value: 0,
            id: '0',
            label: 'Algemeen',
            visible: true,
            subonderdelen: [
                {
                    label: 'Belastingplichtige',
                    pageMapping: 'Belastingplichtige',
                    visible: true,
                    value: 0,
                },
                {
                    label: 'Administratie inlezen',
                    pageMapping: 'Administratie inlezen',
                    visible: !fe,
                    value: 1,
                },
                {
                    label: 'Samenstelling fiscale eenheid',
                    pageMapping: 'Samenstelling fiscale eenheid',
                    visible: fe,
                    value: 2,
                },
                {
                    label: 'Vragen fiscale eenheid',
                    pageMapping: 'Vragen fiscale eenheid',
                    visible: def && fe,
                    value: 3,
                },
                {
                    label: 'Vragen vooraf',
                    pageMapping: 'Vragen vooraf',
                    visible: def,
                    value: 4,
                },
            ],
        },
        {
            value: 1,
            id: '1',
            label: 'Consolidatie',
            visible: def && fe,
            subonderdelen: [
                {
                    label: 'Openingsbalansen activa',
                    pageMapping: 'Openingsbalansen activa',
                    visible: true,
                    value: 0,
                },
                {
                    label: 'Openingsbalansen passiva',
                    pageMapping: 'Openingsbalansen passiva',
                    visible: true,
                    value: 1,
                },
                {
                    label: 'Slotbalansen activa',
                    pageMapping: 'Slotbalansen activa',
                    visible: true,
                    value: 2,
                },
                {
                    label: 'Slotbalansen passiva',
                    pageMapping: 'Slotbalansen passiva',
                    visible: true,
                    value: 3,
                },
                {
                    label: 'Resultatenrekeningen',
                    pageMapping: 'Resultatenrekeningen',
                    visible: true,
                    value: 4,
                },
            ],
        },
        {
            value: 2,
            id: '2',
            label: 'Jaarstukken',
            visible: true,
            subonderdelen: [
                {
                    label: 'Balans activa',
                    pageMapping: 'Balans activa',
                    visible: def,
                    value: 0,
                },
                {
                    label: 'Balans passiva',
                    pageMapping: 'Balans passiva',
                    visible: def,
                    value: 1,
                },
                {
                    label: 'Resultatenrekening',
                    pageMapping: 'Resultatenrekening',
                    visible: true,
                    value: 2,
                },
                {
                    label: 'Vermogensvergelijking',
                    pageMapping: 'Vermogensvergelijking',
                    visible: def,
                    value: 3,
                },
            ],
        },
        {
            value: 3,
            id: '3',
            label: 'Winstcorrecties',
            visible: true,
            subonderdelen: [
                {
                    label: 'Vrijgestelde winst',
                    pageMapping: 'Vrijgestelde winst',
                    visible: def,
                    value: 0,
                },
                {
                    label: 'Beperkt aftrekbare kosten',
                    pageMapping: 'Beperkt aftrekbare kosten',
                    visible: true,
                    value: 1,
                },
                {
                    label: 'Niet aftrekbare boetes',
                    pageMapping: 'Niet aftrekbare boetes',
                    visible: true,
                    value: 2,
                },
                {
                    label: 'Niet aftrekbare rente',
                    pageMapping: 'Niet aftrekbare rente',
                    visible: def,
                    value: 3,
                },
                {
                    label: 'Investeringsaftrek',
                    pageMapping: 'Investeringsaftrek',
                    visible: def,
                    value: 4,
                },
                {
                    label: 'Investeringen overig',
                    pageMapping: 'Investeringen overig',
                    visible: def,
                    value: 5,
                },
                {
                    label: 'Aftrekbare culturele giften',
                    pageMapping: 'Aftrekbare culturele giften',
                    visible: def,
                    value: 6,
                },
                {
                    label: 'Aftrekbare overige giften',
                    pageMapping: 'Aftrekbare overige giften',
                    visible: def,
                    value: 7,
                },
                {
                    label: 'Verliesverrekening',
                    pageMapping: 'Verliesverrekening',
                    visible: true,
                    value: 8,
                },
            ],
        },
        {
            value: 4,
            id: '4',
            label: 'Belastbaar bedrag',
            visible: true,
            subonderdelen: [
                {
                    label: 'Belastbaar bedrag',
                    pageMapping: 'Belastbaar bedrag',
                    visible: true,
                    value: 0,
                },
            ],
        },
        {
            value: 5,
            id: '5',
            label: 'Belastingbedrag',
            visible: true,
            subonderdelen: [
                {
                    label: 'Belastingbedrag',
                    pageMapping: 'Belastingbedrag',
                    visible: true,
                    value: 0,
                },
            ],
        },
        {
            value: 6,
            id: '6',
            label: 'Specificaties',
            visible: def,
            subonderdelen: [
                {
                    label: 'Specificatie Renteaftrekbeperking',
                    pageMapping: 'Specificatie Renteaftrekbeperking',
                    visible: false,
                    value: 0,
                },
                {
                    label: 'Specificatie innovatiebox',
                    pageMapping: 'Specificatie innovatiebox',
                    visible: vpbAangfite.algemeneVragen?.gebruikInnovatiebox ?? false,
                    value: 1,
                },
                {
                    label: 'Specificatie Objectvrijstelling',
                    pageMapping: 'Specificatie Objectvrijstelling',
                    visible: false,
                    value: 2,
                },
                {
                    label: 'Specificatie dividenduitkeringen',
                    pageMapping: 'Specificatie dividenduitkeringen',
                    visible: this.hasDividends.value,
                    value: 3,
                },
                {
                    label: 'Specificatie aandeelhouders',
                    pageMapping: 'Specificatie aandeelhouders',
                    visible: this.isDefAangifte.value,
                    value: 4,
                },
                {
                    label: 'Specificatie agrarische activiteiten',
                    pageMapping: 'Specificatie agrarische activiteiten',
                    visible: vpbAangfite.balansEnResultatenrekening?.agrarischeActiviteiten ?? false,
                    value: 5,
                },
                {
                    label: 'Specificatie willekeurige afschrijvingen',
                    pageMapping: 'Specificatie willekeurige afschrijvingen',
                    visible: vpbAangfite.algemeneVragen?.willekeurigAfschijven ?? false,
                    value: 6,
                },
                {
                    label: 'Specificatie bronheffingen',
                    pageMapping: 'Specificatie bronheffingen',
                    visible: vpbAangfite.belastingBedrag?.aftrekEldersBelast !== undefined,
                    value: 7,
                },
                {
                    label: 'Specificatie deelnemingen',
                    pageMapping: 'Specificatie deelnemingen',
                    visible: this.hasDeelnemingen.value,
                    // visible: this.hasDeelnemingen,
                    value: 8,
                },
                {
                    label: 'Specificatie functionele valuta',
                    pageMapping: 'Specificatie functionele valuta',
                    visible: vpbAangfite.algemeneVragen?.regelingFunctioneleValuta ?? false,
                    value: 10,
                },
                {
                    label: 'Specificatie herinvesteringsreserve',
                    pageMapping: 'Specificatie herinvesteringsreserve',
                    visible: this.hasHir.value,
                    value: 11,
                },
            ],
        },
        {
            value: 7,
            id: '7',
            label: 'Specificaties fiscale eenheid',
            visible: def && fe && feBijz === true,
            subonderdelen: [
                {
                    label: 'Specificatie voeging',
                    pageMapping: 'Specificatie voeging',
                    visible: vpbAangfite.vragenFiscaleEenheid?.voegingMetBestaandeDochtermaatschappijen || false,
                    value: 0,
                },
                {
                    label: 'Specificatie ontvoeging',
                    pageMapping: 'Specificatie ontvoeging',
                    visible: vpbAangfite.vragenFiscaleEenheid?.ontvoegingVanDochtermaatschappijen || false,
                    value: 1,
                },
                {
                    label: 'Specificatie ontbinding',
                    pageMapping: 'Specificatie ontbinding',
                    visible: vpbAangfite.vragenFiscaleEenheid?.beeindigingDoorliquidatieDochter || false,
                    value: 2,
                },
                {
                    label: 'Specificatie verliezen',
                    pageMapping: 'Specificatie verliezen',
                    visible:
                        vpbAangfite.vragenFiscaleEenheid?.verzoekMeegevenVerliezenOntvoegdeDochter ||
                        vpbAangfite.vragenFiscaleEenheid?.verzoekMeegevenVerliezenvanVerdwijnendeDochter ||
                        vpbAangfite.vragenFiscaleEenheid?.verzoekMeegevenVerliezenVerkrijger ||
                        false,
                    value: 3,
                },
            ],
        },
    ];
    this.menuInterface.next([...menuInterface]);
  }

    createBalansEnResultatenRekeningFormGroup(item?: BalansEnResultatenrekening, name?: string) {
        return this.fb.group({
            naamVennootschap: item ? item.naamVennootschap : name,
            boekjaarVennootschap: item ? item.boekjaarVennootschap : (null as unknown as Periode),
            fiscaalNummer: item ? item.fiscaalNummer : (null as unknown as string),
            omschrijvingActiviteiten: item ? item.omschrijvingActiviteiten : 'Activiteiten',
            dochtermaatschappijFiscaleEenheid: item ? item.dochtermaatschappijFiscaleEenheid : true,
            agrarischeActiviteiten: item ? item.agrarischeActiviteiten : false,
            goodwill: this.fb.array(
                item && item.goodwill ? [this.createBalansActivaGroup(item.goodwill[0])] : [this.createBalansActivaGroup()],
            ),
            overigeImmaterieleActiva: this.fb.array(
                item && item.overigeImmaterieleActiva
                    ? [this.createBalansActivaGroup(item.overigeImmaterieleActiva[0])]
                    : [this.createBalansActivaGroup()],
            ),
            gebouwenEnTerreinen: this.fb.array(
                item && item.gebouwenEnTerreinen
                    ? [this.createBalansGebouwenGroup(item.gebouwenEnTerreinen[0])]
                    : [this.createBalansGebouwenGroup()],
            ),
            machinesEnInstallaties: this.fb.array(
                item && item.machinesEnInstallaties
                    ? [this.createBalansMvaGroup(item.machinesEnInstallaties[0])]
                    : [this.createBalansMvaGroup()],
            ),
            andereVasteActiva: this.fb.array(
                item && item.andereVasteActiva ? [this.createBalansMvaGroup(item.andereVasteActiva[0])] : [this.createBalansMvaGroup()],
            ),
            deelnemingen: this.fb.array(
                item && item.deelnemingen ? [this.createBalansActivaGroup(item.deelnemingen[0])] : [this.createBalansActivaGroup()],
            ),
            vorderingenLangGroepsmaatschappijen: this.fb.array(
                item && item.vorderingenLangGroepsmaatschappijen
                    ? [this.createBalansActivaGroup(item.vorderingenLangGroepsmaatschappijen[0])]
                    : [this.createBalansActivaGroup()],
            ),
            vorderingenLangParticipanten: this.fb.array(
                item && item.vorderingenLangParticipanten
                    ? [this.createBalansActivaGroup(item.vorderingenLangParticipanten[0])]
                    : [this.createBalansActivaGroup()],
            ),
            overigeFinancieelVasteActiva: this.fb.array(
                item && item.overigeFinancieelVasteActiva
                    ? [this.createBalansActivaGroup(item.overigeFinancieelVasteActiva[0])]
                    : [this.createBalansActivaGroup()],
            ),
            voorraden: this.fb.array(
                item && item.voorraden != null ? [this.createBalansActivaGroup(item.voorraden[0])] : [this.createBalansActivaGroup()],
            ),
            onderhandenWerk: this.fb.array(
                item && item.onderhandenWerk ? [this.createBalansActivaGroup(item.onderhandenWerk[0])] : [this.createBalansActivaGroup()],
            ),
            vorderingDebiteuren: this.fb.array(
                item && item.vorderingDebiteuren
                    ? [this.createBalansActivaGroup(item.vorderingDebiteuren[0])]
                    : [this.createBalansActivaGroup()],
            ),
            vorderingenOmzetbelasting: this.fb.array(
                item && item.vorderingenOmzetbelasting
                    ? [this.createBalansOmzetbelastingGroup(item.vorderingenOmzetbelasting[0])]
                    : [this.createBalansOmzetbelastingGroup()],
            ),
            vorderingenKortGroepsmaatschappijen: this.fb.array(
                item && item.vorderingenKortGroepsmaatschappijen
                    ? [this.createBalansActivaGroup(item.vorderingenKortGroepsmaatschappijen[0])]
                    : [this.createBalansActivaGroup()],
            ),
            vorderingenKortParticipanten: this.fb.array(
                item && item.vorderingenKortParticipanten
                    ? [this.createBalansActivaGroup(item.vorderingenKortParticipanten[0])]
                    : [this.createBalansActivaGroup()],
            ),
            vorderingenOverig: this.fb.array(
                item && item.vorderingenOverig
                    ? [this.createBalansActivaGroup(item.vorderingenOverig[0])]
                    : [this.createBalansActivaGroup()],
            ),
            effecten: this.fb.array(
                item && item.effecten ? [this.createBalansActivaGroup(item.effecten[0])] : [this.createBalansActivaGroup()],
            ),
            liquideMiddelen: this.fb.array(
                item && item.liquideMiddelen ? [this.createBalansActivaGroup(item.liquideMiddelen[0])] : [this.createBalansActivaGroup()],
            ),
            gestortEnOpgevraagdKapitaal: this.fb.array(
                item && item.gestortEnOpgevraagdKapitaal
                    ? [this.createBalansPassivaGroup(item.gestortEnOpgevraagdKapitaal[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            informeelKapitaal: this.fb.array(
                item && item.informeelKapitaal
                    ? [this.createBalansPassivaGroup(item.informeelKapitaal[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            agioreserve: this.fb.array(
                item && item.agioreserve ? [this.createBalansPassivaGroup(item.agioreserve[0])] : [this.createBalansPassivaGroup()],
            ),
            winstreserves: this.fb.array(
                item && item.winstreserves ? [this.createBalansPassivaGroup(item.winstreserves[0])] : [this.createBalansPassivaGroup()],
            ),
            kostenEgalisatieReserve: this.fb.array(
                item && item.kostenEgalisatieReserve
                    ? [this.createBalansVoorzieningGroup(item.kostenEgalisatieReserve[0])]
                    : [this.createBalansVoorzieningGroup()],
            ),
            herinvesteringsReserve: this.fb.array(
                item && item.herinvesteringsReserve
                    ? [this.createBalansVoorzieningGroup(item.herinvesteringsReserve[0])]
                    : [this.createBalansVoorzieningGroup()],
            ),
            compartimenteringsreserve: this.fb.array(
                item && item.compartimenteringsreserve
                    ? [this.createBalansVoorzieningGroup(item.compartimenteringsreserve[0])]
                    : [this.createBalansVoorzieningGroup()],
            ),
            overigeReserves: this.fb.array(
                item && item.overigeReserves
                    ? [this.createBalansVoorzieningGroup(item.overigeReserves[0])]
                    : [this.createBalansVoorzieningGroup()],
            ),
            garantieVoorzieningen: this.fb.array(
                item && item.garantieVoorzieningen
                    ? [this.createBalansVoorzieningGroup(item.garantieVoorzieningen[0])]
                    : [this.createBalansVoorzieningGroup()],
            ),
            pensioenVoorzieningen: this.fb.array(
                item && item.pensioenVoorzieningen
                    ? [this.createBalansPassivaGroup(item.pensioenVoorzieningen[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            oudedagsVoorzieningen: this.fb.array(
                item && item.oudedagsVoorzieningen
                    ? [this.createBalansPassivaGroup(item.oudedagsVoorzieningen[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            overigeVoorzieningen: this.fb.array(
                item && item.overigeVoorzieningen
                    ? [this.createBalansVoorzieningGroup(item.overigeVoorzieningen[0])]
                    : [this.createBalansVoorzieningGroup()],
            ),
            schuldenLanglopendConvertibles: this.fb.array(
                item && item.schuldenLanglopendConvertibles
                    ? [this.createBalansPassivaGroup(item.schuldenLanglopendConvertibles[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenLanglopendObligaties: this.fb.array(
                item && item.schuldenLanglopendObligaties
                    ? [this.createBalansPassivaGroup(item.schuldenLanglopendObligaties[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenLanglopendGroepsmaatschappijen: this.fb.array(
                item && item.schuldenLanglopendGroepsmaatschappijen
                    ? [this.createBalansPassivaGroup(item.schuldenLanglopendGroepsmaatschappijen[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenLanglopendParticipanten: this.fb.array(
                item && item.schuldenLanglopendParticipanten
                    ? [this.createBalansPassivaGroup(item.schuldenLanglopendParticipanten[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenLanglopendKredietinstellingen: this.fb.array(
                item && item.schuldenLanglopendKredietinstellingen
                    ? [this.createBalansPassivaGroup(item.schuldenLanglopendKredietinstellingen[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenLanglopendOverig: this.fb.array(
                item && item.schuldenLanglopendOverig
                    ? [this.createBalansPassivaGroup(item.schuldenLanglopendOverig[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            crediteuren: this.fb.array(
                item && item.crediteuren ? [this.createBalansPassivaGroup(item.crediteuren[0])] : [this.createBalansPassivaGroup()],
            ),
            schuldenOmzetbelasting: this.fb.array(
                item && item.schuldenOmzetbelasting
                    ? [this.createBalansOmzetbelastingGroup(item.schuldenOmzetbelasting[0])]
                    : [this.createBalansOmzetbelastingGroup()],
            ),
            schuldenLoonheffingen: this.fb.array(
                item && item.schuldenLoonheffingen
                    ? [this.createBalansPassivaGroup(item.schuldenLoonheffingen[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenKortlopendGroepsmaatschappijen: this.fb.array(
                item && item.schuldenKortlopendGroepsmaatschappijen
                    ? [this.createBalansPassivaGroup(item.schuldenKortlopendGroepsmaatschappijen[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenKortlopendParticipanten: this.fb.array(
                item && item.schuldenKortlopendParticipanten
                    ? [this.createBalansPassivaGroup(item.schuldenKortlopendParticipanten[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            schuldenKortlopendOverig: this.fb.array(
                item && item.schuldenKortlopendOverig
                    ? [this.createBalansPassivaGroup(item.schuldenKortlopendOverig[0])]
                    : [this.createBalansPassivaGroup()],
            ),
            nettoOmzet: this.fb.array(
                item && item.nettoOmzet ? [this.createResultatenRekeningGroup(item.nettoOmzet[0])] : [this.createResultatenRekeningGroup()],
            ),
            wijzigingOnderhandenWerk: this.fb.array(
                item && item.wijzigingOnderhandenWerk
                    ? [this.createResultatenRekeningGroup(item.wijzigingOnderhandenWerk[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            geactiveerdeProductieEigenBedrijf: this.fb.array(
                item && item.geactiveerdeProductieEigenBedrijf
                    ? [this.createResultatenRekeningGroup(item.geactiveerdeProductieEigenBedrijf[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            overigeOpbrengsten: this.fb.array(
                item && item.overigeOpbrengsten
                    ? [this.createResultatenRekeningGroup(item.overigeOpbrengsten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            inkoopwaardeVerkopen: this.fb.array(
                item && item.inkoopwaardeVerkopen
                    ? [this.createResultatenRekeningGroup(item.inkoopwaardeVerkopen[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            kostenUitbesteedWerk: this.fb.array(
                item && item.kostenUitbesteedWerk
                    ? [this.createResultatenRekeningGroup(item.kostenUitbesteedWerk[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            lonenEnSalarissen: this.fb.array(
                item && item.lonenEnSalarissen
                    ? [this.createResultatenRekeningGroup(item.lonenEnSalarissen[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            socialeLasten: this.fb.array(
                item && item.socialeLasten
                    ? [this.createResultatenRekeningGroup(item.socialeLasten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            pensioenLasten: this.fb.array(
                item && item.pensioenLasten
                    ? [this.createResultatenRekeningGroup(item.pensioenLasten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            overigePersoneelskosten: this.fb.array(
                item && item.overigePersoneelskosten
                    ? [this.createResultatenRekeningGroup(item.overigePersoneelskosten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            ontvangenVergoedingenEnSubsidies: this.fb.array(
                item && item.ontvangenVergoedingenEnSubsidies
                    ? [this.createResultatenRekeningGroup(item.ontvangenVergoedingenEnSubsidies[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            afschrijvingenGoodwill: this.fb.array(
                item && item.afschrijvingenGoodwill
                    ? [this.createResultatenRekeningGroup(item.afschrijvingenGoodwill[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            afschrijvingOverigeImmaterieleActiva: this.fb.array(
                item && item.afschrijvingOverigeImmaterieleActiva
                    ? [this.createResultatenRekeningGroup(item.afschrijvingOverigeImmaterieleActiva[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            afschrijvingenGebouwen: this.fb.array(
                item && item.afschrijvingenGebouwen
                    ? [this.createResultatenRekeningGroup(item.afschrijvingenGebouwen[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            afschrijvingenMachines: this.fb.array(
                item && item.afschrijvingenMachines
                    ? [this.createResultatenRekeningGroup(item.afschrijvingenMachines[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            afschrijvingenOverig: this.fb.array(
                item && item.afschrijvingenOverig
                    ? [this.createResultatenRekeningGroup(item.afschrijvingenOverig[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            autoEnTransportKosten: this.fb.array(
                item && item.autoEnTransportKosten
                    ? [this.createResultatenRekeningGroup(item.autoEnTransportKosten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            huisvestingsKosten: this.fb.array(
                item && item.huisvestingsKosten
                    ? [this.createResultatenRekeningGroup(item.huisvestingsKosten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            onderhoudsKosten: this.fb.array(
                item && item.onderhoudsKosten
                    ? [this.createResultatenRekeningGroup(item.onderhoudsKosten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            verkoopKosten: this.fb.array(
                item && item.verkoopKosten
                    ? [this.createResultatenRekeningGroup(item.verkoopKosten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            overigeKosten: this.fb.array(
                item && item.overigeKosten
                    ? [this.createResultatenRekeningGroup(item.overigeKosten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            financieleBaten: this.fb.array(
                item && item.financieleBaten
                    ? [this.createResultatenRekeningGroup(item.financieleBaten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            financieleLasten: this.fb.array(
                item && item.financieleLasten
                    ? [this.createResultatenRekeningGroup(item.financieleLasten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            resultaatDeelnemingen: this.fb.array(
                item && item.resultaatDeelnemingen
                    ? [this.createResultatenRekeningGroup(item.resultaatDeelnemingen[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            buitengewoneBaten: this.fb.array(
                item && item.buitengewoneBaten
                    ? [this.createResultatenRekeningGroup(item.buitengewoneBaten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            buitengewoneLasten: this.fb.array(
                item && item.buitengewoneLasten
                    ? [this.createResultatenRekeningGroup(item.buitengewoneLasten[0])]
                    : [this.createResultatenRekeningGroup()],
            ),
            totaal: this.fb.array([this.createTotaalTellingGroup(item)]),
        });
    }

    createBalansActivaGroup(balansActiva?: BalansActiva): FormGroup {
        return this.fb.group({
            openingsbalans: balansActiva ? balansActiva.openingsbalans : (null as unknown as undefined),
            slotbalans: balansActiva ? balansActiva.slotbalans : (null as unknown as undefined),
            nominaleWaardeOfKostenprijs: balansActiva ? balansActiva.nominaleWaardeOfKostenprijs : (null as unknown as undefined),
        });
    }

    createBalansMvaGroup(balansActiva?: BalansMVA): FormGroup {
        return this.fb.group({
            openingsbalans: balansActiva ? balansActiva.openingsbalans : (null as unknown as undefined),
            slotbalans: balansActiva ? balansActiva.slotbalans : (null as unknown as undefined),
            nominaleWaardeOfKostenprijs: balansActiva ? balansActiva.nominaleWaardeOfKostenprijs : (null as unknown as undefined),
            restwaarde: balansActiva ? balansActiva.restwaarde : (null as unknown as undefined),
        });
    }

  createBalansGebouwenGroup(balansActiva?: BalansGebouwen): FormGroup {
    return this.fb.group({
      openingsbalans: balansActiva
        ? balansActiva.openingsbalans
        : (null as unknown as number),
      slotbalans: balansActiva
        ? balansActiva.slotbalans
        : (null as unknown as number),
      nominaleWaardeOfKostenprijs: balansActiva
        ? balansActiva.nominaleWaardeOfKostenprijs
        : (null as unknown as number),
      restwaarde: balansActiva
        ? balansActiva.restwaarde
        : (null as unknown as number),
      inEigenGebruik: balansActiva
        ? balansActiva.inEigenGebruik
        : (true),
      bodemwaarde: balansActiva
        ? balansActiva.bodemwaarde
        : (null as unknown as number)
    });
  }

    createBalansOmzetbelastingGroup(balansOmzetbelasting?: BalansOmzetbelasting): FormGroup {
        return this.fb.group({
            openingsbalans: balansOmzetbelasting ? balansOmzetbelasting.openingsbalans : (null as unknown as number),
            slotbalans: balansOmzetbelasting ? balansOmzetbelasting.slotbalans : (null as unknown as number),
            nominaleWaardeOfKostenprijs: balansOmzetbelasting
                ? balansOmzetbelasting.nominaleWaardeOfKostenprijs
                : (null as unknown as number),
            ditBoekjaar: balansOmzetbelasting ? balansOmzetbelasting.ditBoekjaar : (null as unknown as number),
            vorigeBoekjaar: balansOmzetbelasting ? balansOmzetbelasting.vorigeBoekjaar : (null as unknown as number),
            oudereBoekjaren: balansOmzetbelasting ? balansOmzetbelasting.oudereBoekjaren : (null as unknown as number),
        });
    }

    createBalansPassivaGroup(balansPassiva?: BalansPassiva): FormGroup {
        return this.fb.group({
            openingsbalans: balansPassiva ? balansPassiva.openingsbalans : (null as unknown as number),
            slotbalans: balansPassiva ? balansPassiva.slotbalans : (null as unknown as number),
        });
    }

    createBalansVoorzieningGroup(balansPassiva?: BalansVoorziening): FormGroup {
        return this.fb.group({
            openingsbalans: balansPassiva ? balansPassiva.openingsbalans : (null as unknown as number),
            slotbalans: balansPassiva ? balansPassiva.slotbalans : (null as unknown as number),
            dotatie: balansPassiva ? balansPassiva.dotatie : (null as unknown as number),
            onttrekking: balansPassiva ? balansPassiva.onttrekking : (null as unknown as number),
        });
    }

    createResultatenRekeningGroup(resultatenRekening?: ResultatenRekening): FormGroup {
        return this.fb.group({
            bedrag: resultatenRekening ? resultatenRekening.bedrag : (null as unknown as number),
        });
    }

    createTotaalTellingGroup(balansEnResultaten?: BalansEnResultatenrekening): FormGroup {
        let totaalActivaOpen = this.createTotaalActivaOpen(balansEnResultaten);
        let totaalActivaSlot = this.createTotaalActivaSlot(balansEnResultaten);
        let totaalPassivaOpen = this.createTotaalPassivaOpen(balansEnResultaten);
        let totaalPassivaSlot = this.createTotaalPassivaSlot(balansEnResultaten);
        let totaalResultaat = this.createTotaalResultaat(balansEnResultaten);
        return this.fb.group({
            totaalActivaOpen: totaalActivaOpen,
            totaalActivaSlot: totaalActivaSlot,
            totaalPassivaOpen: totaalPassivaOpen,
            totaalPassivaSlot: totaalPassivaSlot,
            totaalResultaat: totaalResultaat,
        });
    }

    private createTotaalActivaOpen(balansEnResultaten?: BalansEnResultatenrekening): number {
        let totaal = 0;
        if (balansEnResultaten) {
            balansEnResultaten.goodwill?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeImmaterieleActiva?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.gebouwenEnTerreinen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.machinesEnInstallaties?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.andereVasteActiva?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenLangGroepsmaatschappijen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenLangParticipanten?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeFinancieelVasteActiva?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.deelnemingen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.voorraden?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.onderhandenWerk?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingDebiteuren?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenOmzetbelasting?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenKortGroepsmaatschappijen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenKortParticipanten?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenOverig?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.effecten?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.liquideMiddelen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
        }
        return totaal;
    }

    private createTotaalPassivaOpen(balansEnResultaten?: BalansEnResultatenrekening): number {
        let totaal = 0;
        if (balansEnResultaten) {
            balansEnResultaten.gestortEnOpgevraagdKapitaal?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.informeelKapitaal?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.agioreserve?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.winstreserves?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.kostenEgalisatieReserve?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.herinvesteringsReserve?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.compartimenteringsreserve?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeReserves?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.garantieVoorzieningen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.pensioenVoorzieningen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.oudedagsVoorzieningen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeVoorzieningen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendConvertibles?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendObligaties?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendGroepsmaatschappijen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendParticipanten?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendKredietinstellingen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendOverig?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.crediteuren?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenOmzetbelasting?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLoonheffingen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenKortlopendGroepsmaatschappijen?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenKortlopendParticipanten?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenKortlopendOverig?.forEach((item) => {
                totaal += item.openingsbalans || (null as unknown as number);
            });
        }
        return totaal;
    }

    private createTotaalActivaSlot(balansEnResultaten?: BalansEnResultatenrekening): number {
        let totaal = 0;
        if (balansEnResultaten) {
            balansEnResultaten.goodwill?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeImmaterieleActiva?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.gebouwenEnTerreinen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.machinesEnInstallaties?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.andereVasteActiva?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenLangGroepsmaatschappijen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenLangParticipanten?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeFinancieelVasteActiva?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.deelnemingen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.voorraden?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.onderhandenWerk?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingDebiteuren?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenOmzetbelasting?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenKortGroepsmaatschappijen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenKortParticipanten?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.vorderingenOverig?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.effecten?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.liquideMiddelen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
        }
        return totaal;
    }

    private createTotaalPassivaSlot(balansEnResultaten?: BalansEnResultatenrekening): number {
        let totaal = 0;
        if (balansEnResultaten) {
            balansEnResultaten.gestortEnOpgevraagdKapitaal?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.informeelKapitaal?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.agioreserve?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.winstreserves?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.kostenEgalisatieReserve?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.herinvesteringsReserve?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.compartimenteringsreserve?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeReserves?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.garantieVoorzieningen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.pensioenVoorzieningen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.oudedagsVoorzieningen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.overigeVoorzieningen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendConvertibles?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendObligaties?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendGroepsmaatschappijen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendParticipanten?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendKredietinstellingen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLanglopendOverig?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.crediteuren?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenOmzetbelasting?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenLoonheffingen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenKortlopendGroepsmaatschappijen?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenKortlopendParticipanten?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
            balansEnResultaten.schuldenKortlopendOverig?.forEach((item) => {
                totaal += item.slotbalans || (null as unknown as number);
            });
        }
        return totaal;
    }

    private createTotaalResultaat(balansEnResultaten?: BalansEnResultatenrekening): number {
        let totaal = 0;
        if (balansEnResultaten) {
            balansEnResultaten.nettoOmzet?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.wijzigingOnderhandenWerk?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.geactiveerdeProductieEigenBedrijf?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.overigeOpbrengsten?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.inkoopwaardeVerkopen?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.kostenUitbesteedWerk?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.lonenEnSalarissen?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.socialeLasten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.pensioenLasten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.overigePersoneelskosten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.ontvangenVergoedingenEnSubsidies?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.afschrijvingenGoodwill?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.afschrijvingOverigeImmaterieleActiva?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.afschrijvingenGebouwen?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.afschrijvingenMachines?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.afschrijvingenOverig?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.autoEnTransportKosten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.onderhoudsKosten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.verkoopKosten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.overigeKosten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.financieleBaten?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.financieleLasten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.resultaatDeelnemingen?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.buitengewoneBaten?.forEach((item) => {
                totaal += item.bedrag || (null as unknown as number);
            });
            balansEnResultaten.buitengewoneLasten?.forEach((item) => {
                totaal -= item.bedrag || (null as unknown as number);
            });
        }
        return totaal;
    }

  private updateVerzoek(isVerzoek: boolean) {
    this.isVerzoek.next(isVerzoek);
  }

  private createIsVerzoek(corporateTaxEntity: CorporateTaxEntity): boolean {
    return corporateTaxEntity.taxType === 'VA';
  }


}
